import { programNameFromID } from '@/utils/common';
import type { LegacyProgram } from '@/pages/legacy/shared';
import type { ProfileSection } from './queries';
import type { ProfileEntity } from './types';

interface ProgramPresentation {
  sectionLabels?: Partial<Record<ProfileSection['key'], string>>;
  firstGroups: string[];
  deriveCareCode?: boolean;
  caseHearing?: boolean;
}

/** Program differences affect presentation; all sections use the same record operations. */
export const programPresentation: Record<LegacyProgram, ProgramPresentation> = {
  mist: { firstGroups: ['names', 'isnumber', 'ist', 'maxdate', 'dsm'] },
  fist: { firstGroups: ['names', 'isnumber', 'ist', 'ab109', 'commitmentdate', 'completiondate', 'discharge_date'], deriveCareCode: true },
  odrhousing: { firstGroups: ['names', 'bookings', 'hearings', 'court', 'defense_attorney', 'healthplan', 'bhsa_chronic_homeless'] },
  prg_wmn: { firstGroups: ['names', 'bookings', 'hearings', 'court', 'jcit', 'healthplan', 'bhsa_chronic_homeless'] },
  lead: { sectionLabels: { Legal: 'Legal', Enrollment: 'Enrollment and insurance' }, firstGroups: ['names', 'enrolled', 'released', 'pathway', 'pc100136'], caseHearing: true },
  dsh: { firstGroups: ['names', 'ist', 'completiondate', 'facesheet', 'dsm'] },
};

const housingTypes: Partial<Record<LegacyProgram, string[]>> = {
  lead: ['Project-Based', 'Scattered Site', 'Transitional'],
  odrhousing: ['ERC', 'Project-Based', 'Scattered Site', 'PBH+'],
  prg_wmn: ['ERC', 'Project-Based', 'Rapid Rehousing', 'Scattered Site', 'PBH+'],
};
const housingFunding: Partial<Record<LegacyProgram, string[]>> = {
  lead: ['DMH FSP (ODR)', 'Aff Housing Prg', 'LACDA EHV', 'LACDA HCV', 'HACLA EHV', 'HACLA HCV', 'LBHA EHV', 'LBHA HCV', 'Unallocated'],
  odrhousing: ['DMH FSP (ODR)', 'Aff Housing Prg', 'Unallocated', 'LACDA EHV', 'HACLA EHV', 'LACDA HCV'],
  prg_wmn: ['DMH FSP (ODR)', 'Aff Housing Prg', 'Unallocated', 'LACDA EHV', 'HACLA EHV', 'LACDA HCV', 'JCIT RRH'],
};
export const programFieldOptions = (entity: string, field: string, program?: LegacyProgram): string[] | undefined => {
  if (entity !== 'location') return undefined;
  if (field === 'ih_request_status') return ['Status Not Set', 'IH Requested', ...(program !== 'prg_wmn' ? ['ERC Requested'] : []), 'IH Transfer Requested (ODR-H to ODR-H)', 'IH Transfer Requested (CBR to ODR-H)'];
  const choices = ({ psh_type: housingTypes, psh_funding_source: housingFunding } as Record<string, Partial<Record<LegacyProgram, string[]>>>)[field];
  if (!choices) return undefined;
  return program && choices[program] ? choices[program] : Array.from(new Set(Object.values(choices).flat()));
};

const programKeys: Record<string, LegacyProgram> = {
  MIST: 'mist', ODR: 'odrhousing', PRG: 'prg_wmn', FIST: 'fist', DSH: 'dsh', LEAD: 'lead',
};
export const profileProgram = (participant: number | null): LegacyProgram | undefined => {
  if (participant === null || !Number.isSafeInteger(participant) || participant < 1) return undefined;
  return programKeys[programNameFromID(String(participant))];
};

export const orderedProfileEntities = (entities: readonly ProfileEntity[], program?: LegacyProgram): ProfileEntity[] => {
  if (!program) return [...entities];
  const first = programPresentation[program].firstGroups;
  const priority = (name: string) => first.includes(name) ? first.indexOf(name) : first.length;
  return [...entities].sort((left, right) => priority(left.name) - priority(right.name));
};
